"use client";

import { c as _c } from "react/compiler-runtime";
import { Area, AreaChart, Bar, BarChart, Cell, ComposedChart, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type React from "react";
import { MeasurementSourceBadge } from "@/components/patient/measurements/measurement-source-badge";
import type { ChartPanelConfig, MeasurementField, PanelChartType } from "@/types/measurement";
import cartesianElements from "./cartesian-elements";
import type { MeasurementChartPayloadItem, MeasurementChartPointSource } from "./chart-tooltip";
export type TimeSeriesPanelChartType = Exclude<PanelChartType, "SCORE_DONUT">;
export interface ChartField {
  id: string;
  name: string;
  color: string;
}
export type MeasurementPanelChartDatum = Record<string, string | number | null | Record<string, MeasurementChartPointSource> | undefined> & {
  sourceByFieldId?: Record<string, MeasurementChartPointSource>;
};
export interface MeasurementPanelChartProps {
  chartType: TimeSeriesPanelChartType;
  data: MeasurementPanelChartDatum[];
  fields: ChartField[];
  /** Panel config — used for seriesType/stackId overrides in composed charts */
  panel?: ChartPanelConfig;
  /** Field map for tooltip display (maps field ID → MeasurementField) */
  fieldMap?: Map<string, MeasurementField>;
  /** Recharts annotation elements (goal lines, reference ranges) */
  annotations?: React.ReactNode[];
  /** Override cartesian element options (smaller fonts for previews, etc.) */
  cartesianOpts?: {
    tickFontSize?: number;
    yAxisWidth?: number;
    tooltipSize?: "sm" | "xs";
    formatDate?: (date: string, style: "short" | "long") => string;
  };
  /** When true, uses faster animation (600ms). When false, no animation props. */
  animated?: boolean;
}
function getPanelPointSource(item: MeasurementChartPayloadItem): MeasurementChartPointSource | null {
  const sourceByFieldId = item.payload?.sourceByFieldId;
  if (!sourceByFieldId || typeof sourceByFieldId !== "object" || Array.isArray(sourceByFieldId)) {
    return null;
  }
  return (sourceByFieldId as Record<string, MeasurementChartPointSource>)[String(item.dataKey)] ?? null;
}
export default function MeasurementPanelChart(t0) {
  const $ = _c(99);
  const {
    chartType,
    data,
    fields,
    panel,
    fieldMap,
    annotations,
    cartesianOpts,
    animated: t1
  } = t0;
  const animated = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      top: 8,
      right: 8,
      left: 0,
      bottom: 8
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const margin = t2;
  let t3;
  if ($[1] !== animated) {
    t3 = animated ? {
      animationDuration: 600
    } : {};
    $[1] = animated;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const animProps = t3;
  if (chartType === "PIE") {
    const lastEntry = data[data.length - 1];
    const sourceByFieldId = lastEntry?.sourceByFieldId;
    let T0;
    let T1;
    let T2;
    let t10;
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    let t16;
    let t4;
    let t5;
    let t6;
    let t7;
    let t8;
    let t9;
    if ($[3] !== animProps || $[4] !== animated || $[5] !== fields || $[6] !== lastEntry || $[7] !== sourceByFieldId) {
      let t17;
      if ($[24] !== lastEntry || $[25] !== sourceByFieldId) {
        t17 = f => ({
          name: f.name,
          value: Number(lastEntry?.[f.id] || 0),
          color: f.color,
          isUserReported: Boolean(sourceByFieldId?.[f.id]?.isUserReported)
        });
        $[24] = lastEntry;
        $[25] = sourceByFieldId;
        $[26] = t17;
      } else {
        t17 = $[26];
      }
      const pieData = fields.map(t17).filter(_temp);
      T2 = ResponsiveContainer;
      t15 = "100%";
      t16 = "100%";
      T1 = PieChart;
      T0 = Pie;
      t4 = pieData;
      t5 = "value";
      t6 = "name";
      t7 = "50%";
      t8 = "50%";
      t9 = 0;
      t10 = "80%";
      t11 = animProps;
      t12 = animated ? _temp2 : undefined;
      t13 = animated ? false : undefined;
      t14 = pieData.map(_temp3);
      $[3] = animProps;
      $[4] = animated;
      $[5] = fields;
      $[6] = lastEntry;
      $[7] = sourceByFieldId;
      $[8] = T0;
      $[9] = T1;
      $[10] = T2;
      $[11] = t10;
      $[12] = t11;
      $[13] = t12;
      $[14] = t13;
      $[15] = t14;
      $[16] = t15;
      $[17] = t16;
      $[18] = t4;
      $[19] = t5;
      $[20] = t6;
      $[21] = t7;
      $[22] = t8;
      $[23] = t9;
    } else {
      T0 = $[8];
      T1 = $[9];
      T2 = $[10];
      t10 = $[11];
      t11 = $[12];
      t12 = $[13];
      t13 = $[14];
      t14 = $[15];
      t15 = $[16];
      t16 = $[17];
      t4 = $[18];
      t5 = $[19];
      t6 = $[20];
      t7 = $[21];
      t8 = $[22];
      t9 = $[23];
    }
    let t17;
    if ($[27] !== T0 || $[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13 || $[32] !== t14 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
      t17 = <T0 data={t4} dataKey={t5} nameKey={t6} cx={t7} cy={t8} innerRadius={t9} outerRadius={t10} {...t11} label={t12} labelLine={t13}>{t14}</T0>;
      $[27] = T0;
      $[28] = t10;
      $[29] = t11;
      $[30] = t12;
      $[31] = t13;
      $[32] = t14;
      $[33] = t4;
      $[34] = t5;
      $[35] = t6;
      $[36] = t7;
      $[37] = t8;
      $[38] = t9;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Tooltip content={_temp4} />;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    let t19;
    if ($[41] !== T1 || $[42] !== t17) {
      t19 = <T1>{t17}{t18}</T1>;
      $[41] = T1;
      $[42] = t17;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== T2 || $[45] !== t15 || $[46] !== t16 || $[47] !== t19) {
      t20 = <T2 width={t15} height={t16}>{t19}</T2>;
      $[44] = T2;
      $[45] = t15;
      $[46] = t16;
      $[47] = t19;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    return t20;
  }
  const isCursorOff = chartType === "BAR" || chartType === "STACKED_BAR";
  const t4 = isCursorOff ? false : undefined;
  let t5;
  if ($[49] !== cartesianOpts || $[50] !== fieldMap || $[51] !== t4) {
    t5 = cartesianElements({
      fieldMap,
      cursor: t4,
      getPointSource: getPanelPointSource,
      ...cartesianOpts
    });
    $[49] = cartesianOpts;
    $[50] = fieldMap;
    $[51] = t4;
    $[52] = t5;
  } else {
    t5 = $[52];
  }
  const shared = t5;
  let t6;
  if ($[53] !== annotations) {
    t6 = annotations ?? [];
    $[53] = annotations;
    $[54] = t6;
  } else {
    t6 = $[54];
  }
  const annotationNodes = t6;
  if (chartType === "BAR" || chartType === "STACKED_BAR") {
    const stacked = chartType === "STACKED_BAR";
    let t7;
    if ($[55] !== animProps || $[56] !== fields || $[57] !== stacked) {
      let t8;
      if ($[59] !== animProps || $[60] !== stacked) {
        t8 = f_0 => <Bar key={f_0.id} dataKey={f_0.id} fill={f_0.color} fillOpacity={0.85} radius={stacked ? [0, 0, 0, 0] : [2, 2, 0, 0]} stackId={stacked ? "stack" : undefined} name={f_0.id} {...animProps} />;
        $[59] = animProps;
        $[60] = stacked;
        $[61] = t8;
      } else {
        t8 = $[61];
      }
      t7 = fields.map(t8);
      $[55] = animProps;
      $[56] = fields;
      $[57] = stacked;
      $[58] = t7;
    } else {
      t7 = $[58];
    }
    let t8;
    if ($[62] !== annotationNodes || $[63] !== data || $[64] !== shared || $[65] !== t7) {
      t8 = <ResponsiveContainer width="100%" height="100%"><BarChart data={data} margin={margin}>{shared}{annotationNodes}{t7}</BarChart></ResponsiveContainer>;
      $[62] = annotationNodes;
      $[63] = data;
      $[64] = shared;
      $[65] = t7;
      $[66] = t8;
    } else {
      t8 = $[66];
    }
    return t8;
  }
  if (chartType === "AREA") {
    let t7;
    if ($[67] !== animProps || $[68] !== fields) {
      let t8;
      if ($[70] !== animProps) {
        t8 = f_1 => <Area key={f_1.id} type="monotone" dataKey={f_1.id} stroke={f_1.color} fill={f_1.color} fillOpacity={0.15} strokeWidth={2} name={f_1.id} {...animProps} />;
        $[70] = animProps;
        $[71] = t8;
      } else {
        t8 = $[71];
      }
      t7 = fields.map(t8);
      $[67] = animProps;
      $[68] = fields;
      $[69] = t7;
    } else {
      t7 = $[69];
    }
    let t8;
    if ($[72] !== annotationNodes || $[73] !== data || $[74] !== shared || $[75] !== t7) {
      t8 = <ResponsiveContainer width="100%" height="100%"><AreaChart data={data} margin={margin}>{shared}{annotationNodes}{t7}</AreaChart></ResponsiveContainer>;
      $[72] = annotationNodes;
      $[73] = data;
      $[74] = shared;
      $[75] = t7;
      $[76] = t8;
    } else {
      t8 = $[76];
    }
    return t8;
  }
  if (chartType === "COMPOSED") {
    let t7;
    if ($[77] !== animProps || $[78] !== fields || $[79] !== panel?.fieldOverrides) {
      let t8;
      if ($[81] !== animProps || $[82] !== panel?.fieldOverrides) {
        t8 = f_2 => {
          const override = panel?.fieldOverrides?.[f_2.id];
          const seriesType = override?.seriesType ?? "LINE";
          if (seriesType === "BAR") {
            return <Bar key={f_2.id} dataKey={f_2.id} fill={f_2.color} fillOpacity={0.85} radius={[2, 2, 0, 0]} stackId={override?.stackId} name={f_2.id} {...animProps} />;
          }
          if (seriesType === "AREA") {
            return <Area key={f_2.id} type="monotone" dataKey={f_2.id} stroke={f_2.color} fill={f_2.color} fillOpacity={0.15} strokeWidth={2} name={f_2.id} {...animProps} />;
          }
          return <Line key={f_2.id} type="monotone" dataKey={f_2.id} connectNulls={true} stroke={f_2.color} strokeWidth={2} dot={{
            r: 3,
            fill: f_2.color
          }} name={f_2.id} {...animProps} />;
        };
        $[81] = animProps;
        $[82] = panel?.fieldOverrides;
        $[83] = t8;
      } else {
        t8 = $[83];
      }
      t7 = fields.map(t8);
      $[77] = animProps;
      $[78] = fields;
      $[79] = panel?.fieldOverrides;
      $[80] = t7;
    } else {
      t7 = $[80];
    }
    let t8;
    if ($[84] !== annotationNodes || $[85] !== data || $[86] !== shared || $[87] !== t7) {
      t8 = <ResponsiveContainer width="100%" height="100%"><ComposedChart data={data} margin={margin}>{shared}{annotationNodes}{t7}</ComposedChart></ResponsiveContainer>;
      $[84] = annotationNodes;
      $[85] = data;
      $[86] = shared;
      $[87] = t7;
      $[88] = t8;
    } else {
      t8 = $[88];
    }
    return t8;
  }
  let t7;
  if ($[89] !== animProps || $[90] !== fields) {
    let t8;
    if ($[92] !== animProps) {
      t8 = f_3 => <Line key={f_3.id} type="monotone" dataKey={f_3.id} connectNulls={true} stroke={f_3.color} strokeWidth={2} dot={{
        r: 3,
        fill: f_3.color
      }} name={f_3.id} {...animProps} />;
      $[92] = animProps;
      $[93] = t8;
    } else {
      t8 = $[93];
    }
    t7 = fields.map(t8);
    $[89] = animProps;
    $[90] = fields;
    $[91] = t7;
  } else {
    t7 = $[91];
  }
  let t8;
  if ($[94] !== annotationNodes || $[95] !== data || $[96] !== shared || $[97] !== t7) {
    t8 = <ResponsiveContainer width="100%" height="100%"><LineChart data={data} margin={margin}>{shared}{annotationNodes}{t7}</LineChart></ResponsiveContainer>;
    $[94] = annotationNodes;
    $[95] = data;
    $[96] = shared;
    $[97] = t7;
    $[98] = t8;
  } else {
    t8 = $[98];
  }
  return t8;
}
function _temp4(t0) {
  const {
    active,
    payload
  } = t0;
  if (!active || !payload?.length) {
    return null;
  }
  const item = payload[0];
  const isUserReported = Boolean(item.payload?.isUserReported);
  return <div className="rounded-md border border-border bg-background/80 px-3 py-2 text-body shadow-md backdrop-blur-2xl"><span className="font-medium text-foreground">{item.name}: {item.value}</span><MeasurementSourceBadge isUserReported={isUserReported} compact={true} className="mt-1" /></div>;
}
function _temp3(entry, i) {
  return <Cell key={i} fill={entry.color} fillOpacity={0.85} />;
}
function _temp2(t0) {
  const {
    name,
    percent
  } = t0;
  return `${name ?? ""} ${((percent ?? 0) * 100).toFixed(0)}%`;
}
function _temp(d) {
  return d.value > 0;
}